import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import '../../styles/projectcss/Residential.css';

// Import educational project images
import educational1 from '../../assets/images/projects/Educational/1.jpg';
import educational2 from '../../assets/images/projects/Educational/2.jpg';
import educational3 from '../../assets/images/projects/Educational/3.jpeg';
import educational4 from '../../assets/images/projects/Educational/4.jpg';
import educational5 from '../../assets/images/projects/Educational/5.jpeg';
import educational6 from '../../assets/images/projects/Educational/6.jpg';
import educational7 from '../../assets/images/projects/Educational/07.jpg';
import educational8 from '../../assets/images/projects/Educational/08.jpg';
import educational9 from '../../assets/images/projects/Educational/15.jpeg';
import educational10 from '../../assets/images/projects/Educational/10.jpg';
import educational11 from '../../assets/images/projects/Educational/11.jpg';
import educational12 from '../../assets/images/projects/Educational/12.jpg';
import educational13 from '../../assets/images/projects/Educational/13.jpg';
import educational14 from '../../assets/images/projects/Educational/14.jpg';

const Educational = () => {
  const [imageLoading, setImageLoading] = useState({});
  const navigate = useNavigate();

  const educationalProjects = [
    { id: 1, image: educational1 },
    { id: 2, image: educational2 },
    { id: 3, image: educational3 },
    { id: 4, image: educational4 },
    { id: 5, image: educational5 },
    { id: 6, image: educational6 },
    { id: 7, image: educational7 },
    { id: 8, image: educational8 },
    { id: 9, image: educational9 },
    { id: 10, image: educational10 },
    { id: 11, image: educational11 },
    { id: 12, image: educational12 },
    { id: 13, image: educational13 },
    { id: 14, image: educational14 }
  ];

  const projectHighlights = [
    {
      title: "Learning Environments",
      description: "We design educational spaces that inspire learning, creativity, and academic excellence with optimal lighting, acoustics, and functional layouts.",
      icon: "🎓"
    },
    {
      title: "Future-Ready Facilities",
      description: "Our educational projects incorporate modern technology, flexible learning spaces, and sustainable features to prepare students for the future.",
      icon: "🔬"
    },
    {
      title: "Community Impact",
      description: "Every educational facility we build contributes to community development, creating spaces where knowledge is shared and futures are shaped.",
      icon: "🌟"
    }
  ];

  const handleImageLoad = (projectId) => {
    setImageLoading(prev => ({ ...prev, [projectId]: false }));
  };

  const handleImageError = (projectId) => {
    setImageLoading(prev => ({ ...prev, [projectId]: false }));
  };

  const handleInquireProject = (project) => {
    navigate('/contact', { 
      state: { 
        selectedService: "Educational Project",
        projectDetails: project
      }
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const filterVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 200
      }
    }
  };

  return (
    <motion.div 
      className="residential-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="container">
        <motion.div className="residential-header" variants={itemVariants}>
          <h1>Educational Projects</h1>
          <p className="page-intro">
            Explore our portfolio of inspiring educational facilities including schools, colleges, 
            and university buildings designed to foster learning, innovation, and academic excellence.
          </p>

          <motion.div 
            className="projects-filter"
            variants={filterVariants}
          >
            <motion.button
              className="filter-btn"
              onClick={() => navigate('/projects')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              All Projects
            </motion.button>
            <motion.button
              className="filter-btn"
              onClick={() => navigate('/residential')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Residential
            </motion.button>
            <motion.button
              className="filter-btn"
              onClick={() => navigate('/commercial')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Commercial
            </motion.button>
            <motion.button
              className="filter-btn"
              onClick={() => navigate('/industrial')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Industrial
            </motion.button>
            <motion.button
              className="filter-btn"
              onClick={() => navigate('/religious')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Religious
            </motion.button>
            {/* <motion.button
              className="filter-btn"
              onClick={() => navigate('/infrastructure')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Infrastructure
            </motion.button> */}
            <motion.button
              className={`filter-btn ${'educational' === 'educational' ? 'active' : ''}`}
              onClick={() => navigate('/educational')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Educational
            </motion.button>
            <motion.button
              className="filter-btn"
              onClick={() => navigate('/road')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Road
            </motion.button>
          </motion.div>
        </motion.div>

        <motion.section className="gallery-section" variants={itemVariants}>
          <h2>Project Gallery</h2>
          <p className="section-subtitle">A showcase of our inspiring educational constructions</p>
          <div className="gallery-grid">
            {educationalProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className={`gallery-item gallery-item-${index + 1}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="gallery-image">
                  <img 
                    src={project.image} 
                    alt={`Educational Project ${project.id}`}
                    onLoad={() => handleImageLoad(`gallery-${project.id}`)}
                    onError={() => handleImageError(`gallery-${project.id}`)}
                  />
                  <div className="gallery-overlay">
                    {/* <div className="gallery-info">
                      <motion.button 
                        className="inquire-btn"
                        onClick={() => handleInquireProject(project)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        Inquire Now
                      </motion.button>
                    </div> */}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="highlights-section" variants={itemVariants}>
          <h2>Why Choose Our Educational Projects</h2>
          <div className="highlights-grid">
            {projectHighlights.map((highlight, index) => (
              <motion.div
                key={index}
                className="highlight-card"
                whileHover={{ y: -10, transition: { type: "spring", stiffness: 300 } }}
              >
                <div className="highlight-icon">{highlight.icon}</div>
                <h3>{highlight.title}</h3>
                <p>{highlight.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="cta-section" variants={itemVariants}>
          <div className="cta-content">
            <h2>Ready to Build Your Educational Facility?</h2>
            <p>Let's discuss your educational construction needs and create inspiring spaces where future generations can learn and grow.</p>
            <motion.button 
              className="cta-button"
              onClick={() => navigate('/contact')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Your Project
            </motion.button>
          </div>
        </motion.section>
      </div>
    </motion.div>
  );
};

export default Educational;